import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { EnvelopeCheck } from 'react-bootstrap-icons';
import { useAuth } from '../contexts/AuthContext';
import './auth-shared.css';

/* ===========================================================
   Check Inbox Page Component
   Shown after registration to prompt the user to verify their
   email address. Includes a countdown timer, resend link,
   and a button to confirm verification.
   =========================================================== */

/* ---------- Constants ---------- */

/** Total seconds for the countdown timer (10 minutes) */
const INITIAL_COUNTDOWN_SECONDS = 600;

/* ---------- Helper functions ---------- */

/**
 * Formats seconds into MM:SS display string.
 */
function formatTime(totalSeconds: number): string
{
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/* ---------- Main component ---------- */

const CheckInboxNew: React.FC = () =>
{
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { resendVerification } = useAuth();

    /* Extract the email from the query string */
    const email = searchParams.get('email') || '';

    /* -- Countdown timer state -- */
    const [countdown, setCountdown] = useState<number>(INITIAL_COUNTDOWN_SECONDS);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    /* -- Resend state -- */
    const [resending, setResending] = useState<boolean>(false);
    const [resendMessage, setResendMessage] = useState<string | null>(null);

    /* ---------- Countdown timer effect ---------- */

    useEffect(() =>
    {
        timerRef.current = setInterval(() =>
        {
            setCountdown((prev) =>
            {
                if (prev <= 1)
                {
                    if (timerRef.current)
                    {
                        clearInterval(timerRef.current);
                    }
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () =>
        {
            if (timerRef.current)
            {
                clearInterval(timerRef.current);
            }
        };
    }, []);

    /* ---------- Event handlers ---------- */

    /**
     * Navigates to the dashboard after user confirms they verified their email.
     * The backend will check verification status on the next API call.
     */
    function handleVerifiedClick(): void
    {
        navigate('/login');
    }

    /**
     * Resends the verification email via the API.
     */
    const handleResendEmail = useCallback(async (): Promise<void> =>
    {
        if (!email || resending)
        {
            return;
        }

        setResending(true);
        setResendMessage(null);

        try
        {
            const response = await resendVerification(email);
            setResendMessage(response?.message || 'Verification email sent. Please check your inbox.');

            /* Reset the countdown timer */
            setCountdown(INITIAL_COUNTDOWN_SECONDS);
        }
        catch (err: any)
        {
            setResendMessage(err?.response?.data?.detail || 'Failed to resend. Please try again.');
        }
        finally
        {
            setResending(false);
        }
    }, [email, resending, resendVerification]);

    /**
     * Handles click on the backdrop (outside the card) to navigate back.
     */
    function handleBackdropClick(e: React.MouseEvent<HTMLDivElement>): void
    {
        if (e.target === e.currentTarget)
        {
            navigate('/');
        }
    }

    /* ---------- Main render ---------- */

    return (
        <div className="auth-page" onClick={handleBackdropClick}>
            <div className="auth-card verify-card">

                {/* Header icon */}
                <div className="text-center">
                    <div className="verify-icon-wrapper">
                        <EnvelopeCheck size={40} />
                    </div>

                    {/* Heading */}
                    <h2 className="auth-heading">Check your inbox</h2>

                    {/* Subtitle */}
                    <p className="verify-subtitle">
                        We've sent a verification link to
                    </p>

                    {/* Email address (bold) */}
                    <p className="verify-email">{email}</p>
                </div>

                {/* Countdown timer box */}
                <div className="verify-timer-box">
                    <span className="verify-timer-label">Link expires in</span>
                    <span className="verify-timer-value">{formatTime(countdown)}</span>
                </div>

                {/* Verified button */}
                <button
                    type="button"
                    className="auth-submit-btn"
                    onClick={handleVerifiedClick}
                >
                    I've verified my E-mail &rarr;
                </button>

                {/* Resend email link */}
                <div className="text-center">
                    <button
                        type="button"
                        className="verify-resend-link"
                        onClick={handleResendEmail}
                        disabled={resending}
                    >
                        {resending ? 'Sending...' : 'Resend email'}
                    </button>
                </div>

                {/* Resend confirmation message */}
                {resendMessage && (
                    <p className="verify-resend-message">{resendMessage}</p>
                )}

                {/* Hint text */}
                <p className="verify-hint">
                    Didn't receive it? Check your spam or junk folder.
                </p>

            </div>
        </div>
    );
};

export default CheckInboxNew;
